import { useRef, useState } from 'react';
import { OptimusProvider, QueryBoundary, useConfirmationDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { Button, ProgressIndicator, Stack, StackItem } from '@optimus-web/core';
import { PaymentFormRef } from '../../../../../payment/components/forms/ProcessPayment/types';
import { TwoStepProcessPaymentFormModel } from './payerInformationschema';
import { PayerInformationConfigurationFormRef, TwoStepProcessPayerInformation } from './TwoStepProcessPayerInformation';
import { TwoStepProcessPayment } from './TwoStepProcessPayment';

enum PaymentFormStep {
    PayerInformation = 'PayerInformation',
    Payment = 'Payment',
}

interface TwoStepProcessPaymentDialogProps {
    dialogType: 'external' | 'default';
    onClose: () => void;
}

export const TwoStepProcessPaymentDialog = ({ dialogType, onClose }: TwoStepProcessPaymentDialogProps) => {
    const formRef = useRef<PaymentFormRef>(null);
    const payerInfoConfigurationFormRef = useRef<PayerInformationConfigurationFormRef>(null);

    const [activeStep, setActiveStep] = useState(PaymentFormStep.PayerInformation);
    const [isLoading, setIsLoading] = useState(false);
    const [payerAccountId, setPayerAccountId] = useState<string | null>(null);

    const { confirmationDialog, openConfirmationDialog } = useConfirmationDialog({
        onConfirm: () => onClose?.(),
    });

    const handlePayerInformationSubmit = (values: TwoStepProcessPaymentFormModel) => {
        setPayerAccountId(values.payerAccountId);
    };

    const handleSubmit = async () => {
        if (formRef.current) {
            await formRef.current?.submit();
        }
    };

    const handleNextClick = async () => {
        if (payerInfoConfigurationFormRef.current) {
            const isPayerInfoValid = await payerInfoConfigurationFormRef?.current?.validate();
            if (isPayerInfoValid) {
                payerInfoConfigurationFormRef.current.submit();
                setActiveStep(PaymentFormStep.Payment);
            }
        }
    };

    const handleClose = () => {
        const hasDirtyFields =
            payerInfoConfigurationFormRef?.current?.hasDirtyFields || formRef?.current?.hasDirtyFields;

        if (hasDirtyFields) {
            openConfirmationDialog();
        } else {
            onClose?.();
        }
    };

    const ButtonComponent =
        activeStep === PaymentFormStep.Payment ? (
            <Button
                data-test-id="two-step-payment-submit-button"
                variant="primary"
                onClick={handleSubmit}
                type="submit"
                loading={isLoading}
                disabled={isLoading}
            >
                {lt('Submit')}
            </Button>
        ) : (
            <Button
                data-test-id="two-step-payment-next-button"
                variant="primary"
                onClick={handleNextClick}
                type="submit"
            >
                {lt('Next')}
            </Button>
        );

    return (
        <FloatingPanel
            size="medium"
            active
            onClose={handleClose}
            title={lt('ProcessPaymentPanelTitleForOptions')}
            footer={{
                primary: ButtonComponent,
            }}
        >
            <OptimusProvider density="spacious">
                <Stack vertical spacing="400" fill>
                    <ProgressIndicator
                        steps={[
                            {
                                value: PaymentFormStep.PayerInformation,
                                label: 'Payment info',
                                completed: activeStep !== PaymentFormStep.Payment,
                                editable: true,
                            },
                            {
                                value: PaymentFormStep.Payment,
                                label: 'Payment',
                                completed: false,
                                editable: false,
                            },
                        ]}
                        activeStep={activeStep}
                        onStepChange={setActiveStep}
                    />

                    <QueryBoundary loader="inline">
                        <StackItem>
                            {activeStep === PaymentFormStep.PayerInformation ? (
                                <TwoStepProcessPayerInformation
                                    ref={payerInfoConfigurationFormRef}
                                    onSubmit={handlePayerInformationSubmit}
                                />
                            ) : null}
                            {activeStep === PaymentFormStep.Payment && payerAccountId && (
                                <TwoStepProcessPayment
                                    formRef={formRef}
                                    payerAccountId={payerAccountId}
                                    setIsLoading={setIsLoading}
                                    onClose={onClose}
                                    dialogType={dialogType}
                                />
                            )}
                        </StackItem>
                    </QueryBoundary>

                    {confirmationDialog}
                </Stack>
            </OptimusProvider>
        </FloatingPanel>
    );
};
